import { useCallback, useMemo, useState } from "react";
import { useSelectOptions } from "./useSelectOptions";
import { VALIDATION_KEYS } from "../component/csvReadingResult/ResultRowValidation";
import { getRowCsvErrors } from "../component/csvReadingResult/ResultRowCsvValidation";
import { postAddOrderApi } from "../../../../http/api/CsvImportOrderAPI";

const MAX_ROW_COUNT_A_PAGE = 20;

const culculateMaxPage = (wholeRowCount) => {
  return Math.max(
            Math.ceil(wholeRowCount / MAX_ROW_COUNT_A_PAGE),
            1
        );
  }

const createUpdateResultRowValue = (prevResult, valueArray) => {
    
    const updatePropNames = Object.keys(valueArray);

    const newResultValue = {
      ...prevResult.values,
      ...valueArray
    };

    const newChanged = {
      ...prevResult.changed,
      ...Object.fromEntries(updatePropNames.map((prop) => [prop, true]))
    }

    const validations
      = VALIDATION_KEYS
          .filter((vaildation) => 
            vaildation.propsOnChange.some((prop) => updatePropNames.includes(prop))
          );

    const propsToValidate = validations.map((validation) => validation.errorKeyName);

    const validationErrors = validations
          .map((validation) => [validation.errorKeyName, validation.getError(newResultValue)])
          .filter((error) => error[1].length > 0);


    const newErrors = {
      ...Object.fromEntries(
            Object.keys(prevResult.errors)            
              .filter((prop) => !propsToValidate.includes(prop))
              .map((prop) => [prop, prevResult.errors[prop]]
      )),
      ...Object.fromEntries(validationErrors)
    }
    
    return {
      ...prevResult,        
      values: newResultValue,
      changed: newChanged,
      errors: newErrors
    }
  };

const useCsvReadingResult = () => {  

  const [results, setResults] = useState([]);  
  const [page, setPage] = useState(1);
  const [csvCustomerGroupId, setCsvCustomerGroupId] = useState(null);
  const selectOptions = useSelectOptions();

  const maxPage = useMemo(() => culculateMaxPage(results.length), [results.length]);
  
  const movePage = useCallback((page) => {
    setPage(page);
  }, [setPage]);

  const renewResults = useCallback((csvCustomerGroupId, results) => {

    setCsvCustomerGroupId(csvCustomerGroupId);
    
    setResults(
      results.map((result, index) => {  
            
        const values = Object.fromEntries(
                  Object.keys(result)
                      .map((key) => [key, result[key].readValue])                
                );

        const changed = Object.fromEntries(
                  Object.keys(result)
                      .map((key) => [key, false])                
                );

        return ({
            index,
            values,
            changed,
            enabledPreservation: true,
            errors: getRowCsvErrors(result)
        })
      })
    );

    selectOptions.searchShops(csvCustomerGroupId);
    movePage(1);
  }, [setCsvCustomerGroupId, setResults, selectOptions, movePage]);

  const resetResults = useCallback(() => {
    setResults([]);
  }, [setResults]);

  const changeRowValue = useCallback((rowIndex) => (valueArray) =>  {
    
    setResults((prev) => 
      prev.map((row, index) => (
         index === rowIndex
          ? createUpdateResultRowValue(row, valueArray)
          : row        
      ))
    );

  }, [setResults]);

  const changeEnabledPreservation = useCallback((rowIndex) => (enabledPreservation) => {
    
    setResults((prev) => 
      prev.map((row, index) => (
         index === rowIndex
          ? {...row, enabledPreservation}
          : row        
      ))
    );

  }, [setResults]);

  const addOrder = useCallback(async (onSuccess, onFailure) => {
    try {
      await postAddOrderApi({
        orderDetails: 
          results.filter((result) => result.enabledPreservation)
            .map((result) => result.values)
      });
      onSuccess();
    }
    catch (err) {
      onFailure(err);
    }

  }, [results]);

  const hasError = results.some((result) => 
    Object.keys(result.errors).length > 0 && result.enabledPreservation
  );

  return {
    results,
    csvCustomerGroupId,
    renewResults,
    changeRowValue,
    changeEnabledPreservation,
    addOrder,
    resetResults,
    page,
    maxRowCount: MAX_ROW_COUNT_A_PAGE,
    maxPage,
    movePage,
    selectOptions,
    hasError
  };
}

export default useCsvReadingResult;